import { apiService } from './api';
import type { CatalogGateMeta } from './guestBlogSiteService';

export type WalletTransaction = {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  currency: string;
  note: string | null;
  createdAt: string;
};

export type WalletSnapshot = {
  gate: CatalogGateMeta & {
    gated: boolean;
    catalogGateApplies: boolean;
    previewLimit: number;
    unlockMinAmount: number;
    walletBalance: number;
    unlocked: boolean;
    canOrder: boolean;
    addFundsPath: string;
  };
  balance: number;
  unlockMinAmount: number;
  transactions: WalletTransaction[];
};

export type CatalogGateSettings = {
  enabled: boolean;
  previewLimit: number;
  unlockMinAmount: number;
  freeDomains: string[];
};

class WalletService {
  async getWallet(): Promise<WalletSnapshot> {
    const res = await apiService.get<{ success: boolean; data: WalletSnapshot }>('/wallet/me');
    return res.data;
  }

  async getGate(): Promise<CatalogGateMeta> {
    const res = await apiService.get<{ success: boolean; data: CatalogGateMeta }>('/wallet/gate');
    return res.data;
  }

  async createTopUp(amount: number, urls?: { successUrl?: string; cancelUrl?: string }) {
    const res = await apiService.post<{
      success: boolean;
      data: { sessionId: string; url: string; amount: number };
      error?: string;
    }>('/wallet/top-up', {
      amount,
      successUrl: urls?.successUrl,
      cancelUrl: urls?.cancelUrl,
    });
    if (!res.success) {
      throw new Error(res.error || 'Failed to start top-up');
    }
    return res.data;
  }

  async finalizeTopUp(sessionId: string) {
    const res = await apiService.post<{
      success: boolean;
      data: { balanceAfter: number; alreadyApplied: boolean; amount: number; gate: CatalogGateMeta };
    }>('/wallet/finalize', { sessionId });
    return res.data;
  }

  async getSettings(): Promise<CatalogGateSettings> {
    const res = await apiService.get<{ success: boolean; data: CatalogGateSettings }>('/wallet/settings');
    return res.data;
  }

  async updateSettings(payload: Partial<CatalogGateSettings>): Promise<CatalogGateSettings> {
    const res = await apiService.put<{ success: boolean; data: CatalogGateSettings }>(
      '/wallet/settings',
      payload,
    );
    return res.data;
  }

  async getAdminStatus(subjectType: 'client' | 'user', subjectId: string) {
    const res = await apiService.get<{
      success: boolean;
      data: {
        subjectType: 'client' | 'user';
        subjectId: string;
        walletBalance: number;
        catalogUnlockedAt: string | null;
        unlocked: boolean;
        catalogGateApplies: boolean;
        unlockMinAmount: number;
        email?: string | null;
        name?: string | null;
      };
    }>('/wallet/admin/status', { subjectType, subjectId });
    return res.data;
  }

  async adminUnlock(subjectType: 'client' | 'user', subjectId: string) {
    return apiService.post<{ success: boolean; message?: string }>('/wallet/admin/unlock', {
      subjectType,
      subjectId,
    });
  }

  async adminLock(subjectType: 'client' | 'user', subjectId: string) {
    return apiService.post<{
      success: boolean;
      message?: string;
      data?: { unlocked: boolean; reason: string };
    }>('/wallet/admin/lock', {
      subjectType,
      subjectId,
    });
  }

  async adminAdjust(
    subjectType: 'client' | 'user',
    subjectId: string,
    amount: number,
    note?: string,
  ) {
    return apiService.post<{ success: boolean; data: { balanceAfter: number }; error?: string }>(
      '/wallet/admin/adjust',
      { subjectType, subjectId, amount, note },
    );
  }
}

export const walletService = new WalletService();
